import { FC, useCallback, useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';

import { addTransformToCloudinaryUrl, getOptimizedCloudinaryUrl } from 'utils/url';

import 'react-image-lightbox/style.css';

import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { CaseStudyPhotoWithLocalizedTexts } from 'modules/marketing-pages/marketing-page.types';
import LazyLoad from 'react-lazyload';

const PICTURE_THUMBNAIL_SIZE = 152;
const PICTURE_MAX_PERCENTAGE = 100;

interface Props {
  /** in % */
  photoMargin?: number;
  photos?: Array<CaseStudyPhotoWithLocalizedTexts>;
  /** max number of photos displayed in a row */
  rowLimit?: number;
  thumbnailHeight?: number;
}

export const CaseStudyPhotos: FC<Props> = ({
  photos = [],
  photoMargin = 2,
  rowLimit = 2,
  thumbnailHeight,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const photoSize = useMemo(() => `${(PICTURE_MAX_PERCENTAGE - photoMargin) / rowLimit}%`, [
    photoMargin,
    rowLimit,
  ]);
  const { t } = useTranslation('marketingPages');

  const handlePhotoClicked = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  }, []);

  const handleNavigate = useCallback(
    (isNext: boolean) => {
      setCurrentIndex((index) =>
        isNext ? (index + 1) % photos.length : (index + photos.length - 1) % photos.length
      );
    },
    [photos.length]
  );

  const cloudinaryUrl = ({ isThumbnail = false, url }: { isThumbnail?: boolean; url: string }) => {
    if (isThumbnail) {
      return addTransformToCloudinaryUrl(url, {
        width: PICTURE_THUMBNAIL_SIZE,
      });
    }
    return getOptimizedCloudinaryUrl(url, true);
  };

  return (
    <section className="photo-section">
      <div className={styles['photo-grid']}>
        {photos.map(({ url, altText }, index) => {
          const inlinePhotoMargin = `${photoMargin}%`;
          const photoNumber = index + 1;
          const isLastPhotoInRow = photoNumber % rowLimit === 0 || photoNumber === photos.length;
          const photoInlineStyle = {
            marginBottom: inlinePhotoMargin,
            marginRight: isLastPhotoInRow ? 0 : inlinePhotoMargin,
          };
          return (
            <LazyLoad once={true} key={photoNumber}>
              <img
                alt={altText}
                style={{ ...photoInlineStyle }}
                src={cloudinaryUrl({ isThumbnail: true, url })}
                className={styles.photo}
                width={photoSize}
                height={thumbnailHeight ? `${thumbnailHeight}px` : 'auto'}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => handlePhotoClicked(index)}
              />
            </LazyLoad>
          );
        })}
      </div>

      {photos.length > 0 && isOpen && (
        <Lightbox
          wrapperClassName={styles.lightbox}
          enableZoom={false}
          imageCaption={photos[currentIndex].description ?? t('noCaption')}
          mainSrc={cloudinaryUrl({ url: photos[currentIndex].url })}
          nextSrc={cloudinaryUrl({ url: photos[(currentIndex + 1) % photos.length].url })}
          prevSrc={cloudinaryUrl({
            url: photos[(currentIndex + photos.length - 1) % photos.length].url,
          })}
          // eslint-disable-next-line react/jsx-no-bind
          onCloseRequest={() => setIsOpen(false)}
          // eslint-disable-next-line react/jsx-no-bind
          onMovePrevRequest={() => handleNavigate(false)}
          // eslint-disable-next-line react/jsx-no-bind
          onMoveNextRequest={() => handleNavigate(true)}
        />
      )}
    </section>
  );
};

export default CaseStudyPhotos;
export type { Props as CaseStudyPhotosProps };
